import { DRAFT, ageAt, entityGlance, entityTitle, knowledgeAt, refIndex, relationsAt, stateAt, stateEvents, type KnownAt, type KnowWhen, type Presence } from '@dispositio/core';
import type { Editor } from '@tiptap/react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { de } from '../../i18n/de';
import { useEditorDom } from '../../lib/editorDom';
import { ageAtText, changeText } from '../../lib/stateText';
import { useStoryEnv } from '../../state/storyEnv';
import { AssetImg } from '../AssetImg';

/** Verknüpftes Wort bzw. Passage oder bekannter, allein stehender Verweis `[@schlüssel]` */
const REF_SELECTOR = '.rt-ref-link[data-ref], .rt-ref.known[data-ref]';
/** So lange muss die Maus auf einem Verweis ruhen, bis das Panel erscheint (beim Überfahren beim Lesen soll es nicht flackern) */
const HOVER_DELAY = 450;
const HIDE_DELAY = 220;
const WIDTH = 320;

interface Shown {
  key: string;
  /** Lage des Worts im Fenster (bei einer umbrochenen Passage: die Zeile unter der Maus) */
  rect: { left: number; top: number; bottom: number };
  /** angeklickt: bleibt offen, bis man woanders klickt, Esc drückt oder schreibt */
  pinned: boolean;
}

/** Die Zeile des Verweises unter der Maus (eine Passage kann über mehrere Zeilen laufen). */
function rectAt(el: HTMLElement, x: number, y: number): Shown['rect'] {
  const rects = [...el.getClientRects()];
  const r = rects.find((b) => x >= b.left - 1 && x <= b.right + 1 && y >= b.top - 1 && y <= b.bottom + 1) ?? rects[0] ?? el.getBoundingClientRect();
  return { left: r.left, top: r.top, bottom: r.bottom };
}

const presenceClass = (p: Presence): string => `rg-presence ${p}`;
const WHEN_ORDER: Record<KnowWhen, number> = { before: 0, here: 1, later: 2 };
const byWhen = (a: KnownAt, b: KnownAt): number => WHEN_ORDER[a.when ?? 'before'] - WHEN_ORDER[b.when ?? 'before'];

/**
 * Element-Kontext beim Schreiben: Ruht die Maus auf einem verknüpften Wort (bzw. einem Verweis) oder klickt man hinein, erscheint
 * darunter ein kompaktes Panel mit Steckbrief und Bild, den letzten Auftritten in Lesereihenfolge und den Angaben nur für diese
 * Szene. Ein Auftritt öffnet den Draft dieser Szene, „Element öffnen“ das Fenster des Elements – man bleibt im Manuskript.
 * Dazu das Wissen zur Zeit dieser Szene (PLAN.md §16b): was eine Figur hier schon weiß, hier erfährt oder erst später – bzw. beim
 * Gewussten, wer es hier schon weiß, samt dem Leser.
 */
export function RefGlance({ editor, sceneId, disabled }: { editor: Editor; sceneId: string | null; disabled: boolean }) {
  const { ctx, goTo } = useStoryEnv();
  const dom = useEditorDom(editor);
  const panel = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const [shown, setShown] = useState<Shown | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const live = useRef({ shown, disabled });
  live.current = { shown, disabled };

  useEffect(() => {
    if (!dom) return;
    const later = (fn: () => void, ms: number): void => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(fn, ms);
    };
    const refEl = (t: EventTarget | null): HTMLElement | null => (t instanceof Element ? t.closest<HTMLElement>(REF_SELECTOR) : null);
    const over = (e: MouseEvent): void => {
      const el = refEl(e.target);
      const cur = live.current.shown;
      if (!el || live.current.disabled || cur?.pinned) return;
      const key = el.getAttribute('data-ref') ?? '';
      if (cur?.key === key) return void window.clearTimeout(timer.current);
      const { clientX: x, clientY: y } = e;
      later(() => {
        if (el.isConnected && el.matches(':hover')) setShown({ key, rect: rectAt(el, x, y), pinned: false });
      }, HOVER_DELAY);
    };
    const out = (e: MouseEvent): void => {
      if (!refEl(e.target) || live.current.shown?.pinned) return;
      // weiter auf demselben Verweis (z. B. von einem fetten Teil der Passage in den Rest): nichts tun
      if (refEl(e.relatedTarget) && refEl(e.relatedTarget) === refEl(e.target)) return;
      later(() => setShown((s) => (s?.pinned ? s : null)), HIDE_DELAY);
    };
    const click = (e: MouseEvent): void => {
      const el = refEl(e.target);
      // Strg/⌘+Klick öffnet das Element (Editor), eine Markierung mit der Maus ist kein Anklicken
      // (die Auswahl des Fensters: ProseMirror übernimmt sie erst nach dem Klick)
      if (!el || e.ctrlKey || e.metaKey || e.shiftKey || live.current.disabled || window.getSelection()?.isCollapsed === false) return;
      window.clearTimeout(timer.current);
      setShown({ key: el.getAttribute('data-ref') ?? '', rect: rectAt(el, e.clientX, e.clientY), pinned: true });
    };
    // schreiben: das Panel geht aus dem Weg (beim Überfahren schon bei jeder Taste, angeklickt erst bei einer Änderung)
    const key = (): void => {
      if (live.current.shown && !live.current.shown.pinned) setShown(null);
    };
    const onTransaction = ({ transaction }: { transaction: { docChanged: boolean } }): void => {
      if (transaction.docChanged) setShown(null);
    };
    dom.addEventListener('mouseover', over);
    dom.addEventListener('mouseout', out);
    dom.addEventListener('click', click);
    dom.addEventListener('keydown', key);
    editor.on('transaction', onTransaction);
    return () => {
      dom.removeEventListener('mouseover', over);
      dom.removeEventListener('mouseout', out);
      dom.removeEventListener('click', click);
      dom.removeEventListener('keydown', key);
      editor.off('transaction', onTransaction);
      window.clearTimeout(timer.current);
    };
  }, [editor, dom]);

  // Referenz-Browser offen: kein Panel
  useEffect(() => {
    if (disabled) setShown(null);
  }, [disabled]);

  // offen: woanders klicken, Esc, Scrollen oder ein anderes Fenster schließt es
  useEffect(() => {
    if (!shown) return;
    const inside = (t: EventTarget | null): boolean => t instanceof Node && !!panel.current?.contains(t);
    const down = (e: MouseEvent): void => {
      if (inside(e.target) || (e.target instanceof Element && e.target.closest(REF_SELECTOR))) return;
      setShown(null);
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setShown(null);
    };
    const scroll = (e: Event): void => {
      if (!inside(e.target)) setShown(null);
    };
    window.addEventListener('mousedown', down, true);
    window.addEventListener('keydown', esc, true);
    window.addEventListener('scroll', scroll, true);
    window.addEventListener('resize', scroll);
    return () => {
      window.removeEventListener('mousedown', down, true);
      window.removeEventListener('keydown', esc, true);
      window.removeEventListener('scroll', scroll, true);
      window.removeEventListener('resize', scroll);
    };
  }, [shown]);

  const entity = shown ? refIndex(ctx).get(shown.key) : undefined;
  const glance = useMemo(() => (entity ? entityGlance(ctx, entity.id, sceneId) : null), [ctx, entity, sceneId]);
  const know = useMemo(() => (entity && sceneId ? knowledgeAt(ctx, entity.id, sceneId) : null), [ctx, entity, sceneId]);
  const knowing = !!know && (know.knows.length > 0 || know.knownBy.length > 0 || know.reader !== null);
  // Zustand und Beziehungen zur Zeit der Szene (PLAN.md §16c), das Alter aus den Lebensdaten (PF 15) statt „geboren“
  const stand = useMemo(() => {
    if (!entity || !sceneId) return null;
    const all = stateEvents(ctx);
    const at = stateAt(ctx, entity.id, sceneId, all);
    const b = at.before;
    const rows: { key: string; label: string; text: string; sceneId: string | null; here: boolean }[] = [];
    const age = ageAt(ctx, entity.id, sceneId, all);
    if (age) {
      const from = age.state === 'dead' ? age.died : age.born;
      rows.push({ key: 'age', label: from.label, text: `${de.states.age}: ${ageAtText(age)}`, sceneId: from.sceneId || null, here: false });
    }
    if (b.condition && b.condition.value !== 'geboren' && age?.state !== 'dead') rows.push({ key: 'c', label: b.condition.label, text: changeText(ctx, { kind: 'verfassung', value: b.condition.value }), sceneId: b.condition.sceneId || null, here: false });
    if (b.place) rows.push({ key: 'p', label: b.place.label, text: changeText(ctx, { kind: 'ort', value: b.place.value }), sceneId: b.place.sceneId || null, here: false });
    // Besitz von Anfang an (Feld „Besitzer“, AP-31) hat keine Szene
    if (b.holder) rows.push({ key: 'h', label: b.holder.label, text: changeText(ctx, { kind: 'besitz', value: b.holder.value }), sceneId: b.holder.sceneId || null, here: false });
    for (const x of b.holds) rows.push({ key: `i:${x.value}`, label: x.label, text: de.states.has(entityTitle(ctx, x.value)), sceneId: x.sceneId || null, here: false });
    for (const t of b.traits) rows.push({ key: `t:${t.trait}`, label: t.label, text: de.states.trait(t.trait, t.value), sceneId: t.sceneId || null, here: false });
    for (const c of at.here) rows.push({ key: `n:${c.id}`, label: '', text: c.entityId === entity.id ? changeText(ctx, c) : de.states.gets(entityTitle(ctx, c.entityId)), sceneId: null, here: true });
    const rels = relationsAt(ctx, entity.id, sceneId).map((v) => `${v.word} ${entityTitle(ctx, v.otherId)}`);
    return rows.length > 0 || rels.length > 0 ? { rows, rels } : null;
  }, [ctx, entity, sceneId]);

  // unter dem Wort; reicht der Platz nicht, darüber
  useLayoutEffect(() => {
    if (!shown || !panel.current) return setPos(null);
    const h = panel.current.offsetHeight;
    const left = Math.max(8, Math.min(shown.rect.left, window.innerWidth - WIDTH - 8));
    const below = shown.rect.bottom + 6;
    const top = below + h > window.innerHeight - 8 && shown.rect.top - h - 6 >= 8 ? shown.rect.top - h - 6 : below;
    setPos({ left, top });
  }, [shown, glance]);

  if (!shown) return null;
  const keepOpen = (): void => window.clearTimeout(timer.current);
  const leave = (): void => {
    if (shown.pinned) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setShown(null), HIDE_DELAY);
  };
  const openScene = (id: string): void => {
    // aus einer früheren Episode: keine Szene hier
    if (!id) return;
    const scene = ctx.nodes.get(id);
    const draft = scene?.documents.find((d) => d.name === DRAFT) ?? scene?.documents[0];
    setShown(null);
    goTo({ nodeId: id, docId: draft?.id ?? null });
  };
  const style: CSSProperties = { width: WIDTH, left: pos?.left ?? shown.rect.left, top: pos?.top ?? shown.rect.bottom + 6, visibility: pos ? 'visible' : 'hidden' };

  return (
    <div
      ref={panel}
      className="ref-glance"
      role="dialog"
      aria-label={de.glance.label(glance?.title ?? `@${shown.key}`)}
      style={style}
      onMouseEnter={keepOpen}
      onMouseLeave={leave}
      onMouseDown={(e) => {
        // der Cursor bleibt im Text
        if (!(e.target instanceof HTMLElement && e.target.closest('button'))) e.preventDefault();
      }}
      data-testid="ref-glance"
      data-ref={shown.key}
    >
      {!glance ? (
        <div className="muted small">{de.glance.unknown(shown.key)}</div>
      ) : (
        <>
          <div className="rg-head">
            <span className="rb-avatar rg-avatar" style={{ '--c': glance.typeColor } as CSSProperties}>
              {glance.imageId ? <AssetImg id={glance.imageId} alt="" data-testid="ref-glance-image" /> : <span>{glance.title.slice(0, 1).toUpperCase()}</span>}
            </span>
            <div className="rg-main">
              <div className="rg-title" data-testid="ref-glance-title">
                {glance.title}
              </div>
              <div className="rg-sub">
                <span className="rb-dot" style={{ background: glance.typeColor }} aria-hidden="true" />
                {glance.typeName} · @{glance.refKey}
              </div>
            </div>
          </div>
          {glance.summary && <p className="rg-summary">{glance.summary}</p>}
          {glance.fields.length > 0 && (
            <dl className="rg-fields" data-testid="ref-glance-fields">
              {glance.fields.map((f) => (
                <div key={f.label}>
                  <dt>{f.label}</dt>
                  <dd>{f.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {glance.scene && (
            <section className="rg-section" data-testid="ref-glance-scene">
              <h4>
                <span className="grow">{de.glance.here(glance.scene.label)}</span>
                {glance.scene.presence && <span className={presenceClass(glance.scene.presence)}>{de.glance.presence[glance.scene.presence]}</span>}
              </h4>
              {glance.scene.fields.length > 0 ? (
                <dl className="rg-fields">
                  {glance.scene.fields.map((f, i) => (
                    <div key={`${f.label}-${i}`}>
                      <dt>{f.label}</dt>
                      <dd>{f.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <div className="muted small">{glance.scene.presence ? de.glance.noOwn : de.glance.notYet}</div>
              )}
            </section>
          )}
          {stand && (
            <section className="rg-section" data-testid="ref-glance-state">
              <h4>{de.states.glance}</h4>
              <ol className="rg-recent">
                {stand.rows.map((r) => (
                  <li key={r.key}>
                    <button type="button" className="rg-scene" disabled={!r.sceneId} onClick={() => r.sceneId && openScene(r.sceneId)} data-testid="ref-glance-state-row" data-here={r.here}>
                      <span className="rg-label">{r.label || '·'}</span>
                      <span className="rg-scene-title">{r.text}</span>
                      {r.here && <span className="rg-know here">{de.states.here}</span>}
                    </button>
                  </li>
                ))}
              </ol>
              {stand.rels.length > 0 && (
                <div className="small muted" data-testid="ref-glance-relations">
                  {stand.rels.join(' · ')}
                </div>
              )}
            </section>
          )}
          {know && knowing && (
            <section className="rg-section" data-testid="ref-glance-know">
              <h4>{de.glance.know}</h4>
              <ol className="rg-recent">
                {[...know.knows].sort(byWhen).map((k) => (
                  <li key={`k:${k.about ?? k.text}`}>
                    <button type="button" className="rg-scene" title={k.text} onClick={() => openScene(k.sceneId)} data-testid="ref-glance-knows" data-when={k.when}>
                      <span className="rg-label">{k.label || '·'}</span>
                      <span className="rg-scene-title">{k.about ? entityTitle(ctx, k.about) : `„${k.text}“`}</span>
                      <span className={`rg-know ${k.when}`}>{de.glance.knowWhen[k.when ?? 'before']}</span>
                    </button>
                  </li>
                ))}
                {[...know.knownBy].sort(byWhen).map((k) => (
                  <li key={`b:${k.knower}`}>
                    <button type="button" className="rg-scene" title={k.text} onClick={() => openScene(k.sceneId)} data-testid="ref-glance-known-by" data-when={k.when}>
                      <span className="rg-label">{k.label || '·'}</span>
                      <span className="rg-scene-title">{entityTitle(ctx, k.knower!)}</span>
                      <span className={`rg-know ${k.when}`}>{de.glance.knowWhen[k.when ?? 'before']}</span>
                    </button>
                  </li>
                ))}
                {know.reader ? (
                  <li>
                    <button type="button" className="rg-scene" title={know.reader.text} onClick={() => openScene(know.reader!.sceneId)} data-testid="ref-glance-reader" data-when={know.reader.when ?? ''}>
                      <span className="rg-label">{know.reader.label || '·'}</span>
                      <span className="rg-scene-title">{de.glance.reader}</span>
                      <span className={`rg-know ${know.reader.when ?? 'before'}`}>{know.reader.when ? de.glance.knowWhen[know.reader.when] : de.glance.revealed}</span>
                    </button>
                  </li>
                ) : (
                  know.knownBy.length > 0 && (
                    <li className="muted small" data-testid="ref-glance-reader-never">
                      {de.glance.readerNever}
                    </li>
                  )
                )}
              </ol>
            </section>
          )}
          <section className="rg-section" data-testid="ref-glance-recent">
            <h4>{glance.scene?.label ? de.glance.recent : de.glance.recentAll}</h4>
            {glance.recent.length === 0 ? (
              <div className="muted small">{glance.scene?.label ? de.glance.firstHere : de.glance.none}</div>
            ) : (
              <ol className="rg-recent">
                {glance.earlier > glance.recent.length && <li className="muted small">{de.glance.more(glance.earlier - glance.recent.length)}</li>}
                {glance.recent.map((a) => (
                  <li key={a.sceneId}>
                    <button type="button" className="rg-scene" title={de.glance.openScene(a.title)} onClick={() => openScene(a.sceneId)} data-testid="ref-glance-appearance" data-scene={a.sceneId}>
                      <span className="rg-label">{a.label}</span>
                      <span className="rg-scene-title">{a.title}</span>
                      <span className={presenceClass(a.presence)}>{de.glance.presence[a.presence]}</span>
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </section>
          <div className="rg-foot">
            <span className="muted small grow">{de.glance.hint}</span>
            <button
              type="button"
              className="btn small"
              onClick={() => {
                setShown(null);
                goTo({ nodeId: null, entityId: glance.entityId });
              }}
              data-testid="ref-glance-open"
            >
              {de.glance.open}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
